/**
 * How a chapter arranges what it holds.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **To add a column count or an alignment, add a row to the table below.**
 * The type, the list of ids and the picker all follow from it.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * A chapter is a column of sections one under another, which is right for
 * prose and wrong for a good deal else: four key figures, two charts side by
 * side, a row of three datapoints. Each of those is one idea shown in parts,
 * and stacking the parts down the page turns a comparison into a scroll.
 *
 * So a chapter can lay its sections out in columns. Two things follow from
 * that and neither is obvious:
 *
 * **The chapter's own heading never joins the grid.** It belongs to everything
 * under it, so it spans the full measure whatever the columns are doing.
 *
 * **Document order does not change.** Two columns means the first section is
 * top left and the second is top right, read across then down, which is how
 * every other part of the application already treats the tree: the outline,
 * the contents, the export and the reading order for anybody using a screen
 * reader. A layout that changed the order would make those four disagree.
 *
 * Pure. Counts and names, no rendering.
 */

/**
 * How many sections sit side by side.
 *
 * Stops at four. A5 and A4 are the papers this prints on, and a fifth column
 * on A4 leaves each section about thirty millimetres wide, which is narrower
 * than a table of two figures can be set.
 */
export const GROUP_COLUMNS = [1, 2, 3, 4] as const

export type GroupColumns = (typeof GROUP_COLUMNS)[number]

export const DEFAULT_GROUP_COLUMNS: GroupColumns = 1

/**
 * How sections of different heights line up across a row.
 *
 * Only visible once there are columns, which is why it is offered beside them
 * rather than on its own. The default stretches, because two figures side by
 * side in boxes of different heights read as a mistake rather than as a pair.
 */
export const GROUP_ALIGNMENTS = {
  /** Equal height, whichever is tallest. */
  stretch: 'stretch',
  /** Each at its natural height, tops level. */
  start: 'start',
  /** Each at its natural height, centred against the tallest. */
  center: 'center',
} as const

export type GroupAlign = keyof typeof GROUP_ALIGNMENTS

export const GROUP_ALIGN_IDS = Object.keys(GROUP_ALIGNMENTS) as readonly GroupAlign[]

export const DEFAULT_GROUP_ALIGN: GroupAlign = 'stretch'

/**
 * A stored column count, if it is one this version offers.
 *
 * Read forgivingly, like everything else off a document: a chapter saved by a
 * version that offered six columns opens at four rather than not at all.
 */
export function readColumns(value: unknown): GroupColumns {
  const found = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(found)) return DEFAULT_GROUP_COLUMNS

  const clamped = Math.round(found)
  return (GROUP_COLUMNS as readonly number[]).includes(clamped)
    ? (clamped as GroupColumns)
    : DEFAULT_GROUP_COLUMNS
}

export function readAlign(value: unknown): GroupAlign {
  return typeof value === 'string' && value in GROUP_ALIGNMENTS
    ? (value as GroupAlign)
    : DEFAULT_GROUP_ALIGN
}

/** What the grid should be told, for a chapter laid out this way. */
export function gridStyle(columns: GroupColumns, align: GroupAlign): Record<string, string> {
  return {
    // `minmax(0, 1fr)` rather than `1fr`: a wide table inside a column would
    // otherwise push that column past its share and unbalance the row.
    '--group-columns': `repeat(${columns}, minmax(0, 1fr))`,
    '--group-align': GROUP_ALIGNMENTS[align],
  }
}
